/**
 * UserAutocomplete Component
 * 
 * Input field with user search dropdown.
 * 
 * @package PresszoneComments
 */

import { el, __ } from '../utils/dom.js';
import API from '../utils/api.js';

/**
 * User Autocomplete Input
 * 
 * @param {Object} props
 * @param {string} props.id - Input ID
 * @param {string} props.placeholder - Input placeholder
 * @param {Function} props.onSelect - Callback(user) when user is selected
 * @param {Object} props.containerStyle - Style for the wrapper
 * @param {Object} props.inputProps - Extra props for input
 */
export function UserAutocomplete({ id, placeholder, onSelect, containerStyle = {}, inputProps = {}, extraClass = '' }) {
    let searchTimeout = null;
    const dropdownId = `${id}-dropdown`;

    // Determine input class - allow override or default
    const inputClass = (inputProps.class || 'presszone-comments-input') + (extraClass ? ` ${extraClass}` : '');
    // Remove class from inputProps to avoid duplicate attribute
    const { class: _, ...otherInputProps } = inputProps;

    const input = el('input', {
        type: 'text',
        id: id,
        class: inputClass,
        placeholder: placeholder || __('Search user...', 'comments-press-zone'),
        autocomplete: 'off',
        ...otherInputProps,
        oninput: (e) => {
            const query = e.target.value.trim();
            if (searchTimeout) clearTimeout(searchTimeout);

            if (query.length < 2) {
                hideDropdown();
                return;
            }

            searchTimeout = setTimeout(() => searchUsers(query), 300);
        },
        onfocus: (e) => {
            if (e.target.value.trim().length >= 2) {
                searchUsers(e.target.value.trim());
            }
        }
    });

    const dropdown = el('div', {
        id: dropdownId,
        class: 'presszone-comments-autocomplete__dropdown presszone-comments-animated-item--pop'
    });

    const wrapper = el('div', {
        class: 'presszone-comments-autocomplete'
    }, input, dropdown);

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
        if (!wrapper.contains(e.target)) {
            hideDropdown();
        }
    });

    function hideDropdown() {
        dropdown.classList.remove('is-visible');
    }

    function showDropdown() {
        dropdown.classList.add('is-visible');
    }

    function setDropdownContent(element) {
        while (dropdown.firstChild) dropdown.removeChild(dropdown.firstChild);
        dropdown.appendChild(element);
    }

    async function searchUsers(query) {
        setDropdownContent(el('div', { class: 'presszone-comments-autocomplete__message' }, __('Searching...', 'comments-press-zone')));
        showDropdown();

        try {
            const users = await API.get(`/users?search=${encodeURIComponent(query)}&limit=10`);

            if (!users || users.length === 0) {
                setDropdownContent(el('div', { class: 'presszone-comments-autocomplete__message' }, __('No users found', 'comments-press-zone')));
                return;
            }

            const fragment = document.createDocumentFragment();
            users.forEach(user => {
                const item = el('div', {
                    class: 'presszone-comments-autocomplete__item',
                    onmousedown: (e) => {
                        e.preventDefault();
                        hideDropdown();
                        input.value = `${user.display_name || user.name} (@${user.username || user.user_login})`;
                        if (onSelect) onSelect(user);
                    }
                },
                    el('img', {
                        src: user.avatar || user.avatar_url || '',
                        class: 'presszone-comments-autocomplete__avatar'
                    }),
                    el('div', { class: 'presszone-comments-autocomplete__info' },
                        el('div', { class: 'presszone-comments-autocomplete__name' }, user.display_name || user.name),
                        el('div', { class: 'presszone-comments-autocomplete__username' }, '@' + (user.username || user.user_login))
                    )
                );
                fragment.appendChild(item);
            });
            while (dropdown.firstChild) dropdown.removeChild(dropdown.firstChild);
            dropdown.appendChild(fragment);
        } catch (error) {
            setDropdownContent(el('div', { class: 'presszone-comments-autocomplete__message presszone-comments-autocomplete__message--error' }, __('Search failed', 'comments-press-zone')));
            console.error('User search error:', error);
        }
    }

    return wrapper;
}

export default UserAutocomplete;
